import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import FadeIn from '../components/FadeIn';
import { cloudinaryUrl } from '../utils/cloudinary';
import ProductCard from '../components/ProductCard';
import { formatPrice } from '../utils/formatPrice';
import { CATEGORY_LABELS } from '../utils/constants';
import SEO from '../components/SEO';
import allProducts from '../data/products.json';

export function hasSpecs(p) {
  return [p.spec_wheel_size, p.spec_weight_kg, p.spec_gears, p.spec_brake_front,
          p.spec_brake_rear, p.spec_motor_w, p.spec_battery_v, p.spec_battery_ah,
          p.spec_range_km, p.spec_max_speed].some(v => v != null);
}

export function specRows(p) {
  const rows = [];
  if (p.spec_wheel_size != null)  rows.push({ label: 'Kerékméret',          value: p.spec_wheel_size });
  if (p.spec_weight_kg != null)   rows.push({ label: 'Súly',                value: `${p.spec_weight_kg} kg` });
  if (p.spec_gears != null)       rows.push({ label: 'Váltó',               value: p.spec_gears });
  if (p.spec_brake_front != null) rows.push({ label: 'Első fék',            value: p.spec_brake_front });
  if (p.spec_brake_rear != null)  rows.push({ label: 'Hátsó fék',           value: p.spec_brake_rear });
  if (p.spec_motor_w != null)     rows.push({ label: 'Motor teljesítmény',  value: `${p.spec_motor_w} W` });
  if (p.spec_battery_v != null || p.spec_battery_ah != null) {
    const parts = [
      p.spec_battery_v  != null ? `${p.spec_battery_v}V`  : null,
      p.spec_battery_ah != null ? `${p.spec_battery_ah}Ah` : null,
    ].filter(Boolean);
    rows.push({ label: 'Akkumulátor', value: parts.join(' / ') });
  }
  if (p.spec_range_km != null)   rows.push({ label: 'Hatótáv',      value: `${p.spec_range_km} km` });
  if (p.spec_max_speed != null)  rows.push({ label: 'Max sebesség', value: `${p.spec_max_speed} km/h` });
  return rows;
}

export default function ProductDetail() {
  const { slug }   = useParams();
  const navigate   = useNavigate();
  const [activeImg, setActiveImg] = useState(0);

  const product = useMemo(() => allProducts.find(p => p.slug === slug) || null, [slug]);

  const similar = useMemo(() => {
    if (!product) return [];
    return allProducts.filter(p => p.category_slug === product.category_slug && p.slug !== slug).slice(0, 4);
  }, [product, slug]);

  // Recently viewed
  useEffect(() => {
    if (!product) return;
    try {
      const prev = JSON.parse(localStorage.getItem('recently_viewed') || '[]');
      const filtered = prev.filter(p => p.slug !== product.slug);
      localStorage.setItem('recently_viewed', JSON.stringify([product, ...filtered].slice(0, 10)));
    } catch {}
  }, [product]);

  useEffect(() => { setActiveImg(0); }, [slug]);

  const images = product
    ? [product.image_url, product.image2_url, product.image3_url, product.image4_url]
        .filter(Boolean)
        .map(url => cloudinaryUrl(url, { width: 1200 }))
    : [];

  if (!product) return (
    <main className="page">
      <div className="container" style={{ paddingTop: 80 }}>
        <div className="empty-state">
          <div className="empty-state__icon">😕</div>
          <h2 className="empty-state__title">A termék nem található</h2>
          <p style={{ marginBottom: 'var(--sp-6)' }}>Ez a kerékpár már nem elérhető.</p>
          <button className="btn btn-primary" onClick={() => navigate('/kerekparok')}>Vissza a kínálathoz</button>
        </div>
      </div>
    </main>
  );

  const isOutOfStock = product.stock === 0;
  const isElektromos = product.category_slug === 'elektromos';

  const productSchema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: product.description || `${product.name} – prémium kerékpár a Cruiser Shop webáruháztól.`,
    image: product.image_url,
    sku: product.slug,
    brand: { '@type': 'Brand', name: 'Cruiser Shop' },
    offers: {
      '@type': 'Offer',
      url: `https://kerekparszakuzletbaja.hu/kerekparok/${product.slug}`,
      priceCurrency: 'HUF',
      price: product.price,
      availability: product.stock > 0 ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
      seller: { '@type': 'Organization', name: 'Cruiser Shop' },
    },
    aggregateRating: { '@type': 'AggregateRating', ratingValue: '4.9', reviewCount: '127', bestRating: '5' },
  };

  return (
    <>
      <SEO title={product.name} description={product.description} image={product.image_url} url={`/kerekparok/${product.slug}`} type="product" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(productSchema) }} />
      <main className="page">
        <div style={{ background: 'var(--white)', borderBottom: '1px solid var(--border)', padding: 'var(--sp-4) 0' }}>
          <div className="container">
            <div className="breadcrumb">
              <Link to="/">Kezdőlap</Link>
              <span className="breadcrumb__sep">›</span>
              <Link to="/kerekparok">Kerékpárok</Link>
              {product.category_slug && (
                <><span className="breadcrumb__sep">›</span>
                <Link to={`/kerekparok?category=${product.category_slug}`}>{CATEGORY_LABELS[product.category_slug] || product.category_name}</Link></>
              )}
              <span className="breadcrumb__sep">›</span>
              <span style={{ color: 'var(--text)', fontWeight: 500 }}>{product.name}</span>
            </div>
          </div>
        </div>

        <div className="container" style={{ padding: 'var(--sp-10) var(--container-pad) var(--sp-16)' }}>
          <div className="pd-layout">
            <FadeIn direction="left">
              <div>
                <div className="pd-main-img" style={{ boxShadow: 'var(--shadow-md)' }}>
                  {images.length > 0 ? (
                    <img src={images[activeImg]} alt={product.name} />
                  ) : (
                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg,var(--coral-light),var(--peach-light))' }}>
                      <svg width="80" height="80" viewBox="0 0 64 64" fill="none" opacity="0.3">
                        <circle cx="16" cy="44" r="12" stroke="var(--coral)" strokeWidth="3"/>
                        <circle cx="48" cy="44" r="12" stroke="var(--coral)" strokeWidth="3"/>
                        <path d="M16 44L28 20L36 28L42 20L48 44" stroke="var(--coral)" strokeWidth="3" fill="none" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  )}
                </div>
                {images.length > 1 && (
                  <div className="pd-thumb-grid">
                    {images.map((img, i) => (
                      <div key={i} className={`pd-thumb${activeImg === i ? ' active' : ''}`} onClick={() => setActiveImg(i)}>
                        <img src={img} alt={`${product.name} ${i + 1}`} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </FadeIn>

            <FadeIn direction="right">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-5)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  {product.category_slug && (
                    <Link to={`/kerekparok?category=${product.category_slug}`}>
                      <span className="badge badge-peach">{CATEGORY_LABELS[product.category_slug] || product.category_name}</span>
                    </Link>
                  )}
                </div>

                <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(1.6rem,3vw,2.2rem)', fontWeight: 700, color: 'var(--text)', lineHeight: 1.15, letterSpacing: '-0.02em' }}>
                  {product.name}
                </h1>


                {product.description && (
                  <p style={{ fontSize: 'var(--fs-md)', color: 'var(--text-muted)', lineHeight: 1.75 }}>{product.description}</p>
                )}

                <div className="divider" style={{ margin: 'var(--sp-2) 0' }} />

                {isElektromos ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
                    <div style={{ background: '#EEF2FF', borderRadius: 'var(--r-xl)', padding: 'var(--sp-4) var(--sp-5)', display: 'flex', alignItems: 'flex-start', gap: 'var(--sp-3)' }}>
                      <span style={{ fontSize: 22 }}>⚡</span>
                      <div>
                        <div style={{ fontWeight: 700, color: '#3730A3', fontSize: 'var(--fs-md)', marginBottom: 4 }}>Egyedi ajánlat szükséges</div>
                        <div style={{ fontSize: 'var(--fs-sm)', color: '#4338CA', lineHeight: 1.6 }}>
                          Ez az elektromos kerékpár személyes tanácsadást igényel. Vegye fel velünk a kapcsolatot és készítünk Önnek egyedi ajánlatot!
                        </div>
                      </div>
                    </div>
                    <a href="/kapcsolat" className="btn btn-primary btn-lg" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--sp-2)', textDecoration: 'none' }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                        <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.63A2 2 0 012 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 14z"/>
                      </svg>
                      Kapcsolatfelvétel
                    </a>
                  </div>
                ) : isOutOfStock ? (
                  <div style={{ background: 'var(--error-bg)', borderRadius: 'var(--r-md)', padding: 'var(--sp-4)', color: 'var(--error)', fontWeight: 600, fontSize: 'var(--fs-sm)' }}>
                    Ez a termék jelenleg nem elérhető.
                  </div>
                ) : (
                  <a href="/kapcsolat" className="btn btn-primary btn-lg" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--sp-2)', textDecoration: 'none' }}>
                    Érdeklődöm →
                  </a>
                )}
              </div>
            </FadeIn>
          </div>

          {product.description && (
            <div style={{ display: 'flex', borderRadius: 'var(--r-xl)', overflow: 'hidden', marginTop: 'var(--sp-8)', boxShadow: 'var(--shadow-sm)' }}>
              <div style={{ width: 5, flexShrink: 0, background: 'var(--coral)' }} />
              <div style={{ flex: 1, background: 'var(--peach-light)', padding: 'var(--sp-8)', position: 'relative', overflow: 'hidden' }}>
                <span style={{ position: 'absolute', top: -10, right: 24, fontSize: 120, lineHeight: 1, color: 'var(--coral)', opacity: 0.12, fontFamily: 'Georgia, serif', userSelect: 'none', pointerEvents: 'none' }}>"</span>
                <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--fs-xl)', fontWeight: 700, color: 'var(--coral)', marginBottom: 'var(--sp-3)' }}>Leírás</h2>
                <p style={{ fontSize: 'var(--fs-md)', color: 'var(--text-2)', lineHeight: 1.8, whiteSpace: 'pre-line', position: 'relative' }}>{product.description}</p>
              </div>
            </div>
          )}

          {hasSpecs(product) && (
            <div style={{ background: 'var(--white)', borderRadius: 'var(--r-xl)', padding: 'var(--sp-8)', marginTop: 'var(--sp-6)', marginBottom: 'var(--sp-8)', boxShadow: 'var(--shadow-sm)' }}>
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--fs-xl)', fontWeight: 700, color: 'var(--text)', marginBottom: 'var(--sp-6)' }}>Műszaki adatok</h2>
              <div className="spec-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 'var(--sp-3)' }}>
                {specRows(product).map(({ label, value }, i) => {
                  const ICONS = {
                    'Kerékméret': { icon: '🔵', bg: '#EFF6FF', border: '#BFDBFE' },
                    'Súly':       { icon: '⚖️', bg: '#F0FDF4', border: '#BBF7D0' },
                    'Váltó':      { icon: '⚙️', bg: '#F5F3FF', border: '#DDD6FE' },
                    'Első fék':   { icon: '🛑', bg: '#FFF7ED', border: '#FED7AA' },
                    'Hátsó fék':  { icon: '🛑', bg: '#FFF7ED', border: '#FED7AA' },
                    'Motor teljesítmény': { icon: '⚡', bg: '#FEFCE8', border: '#FDE68A' },
                    'Akkumulátor': { icon: '🔋', bg: '#F0FDF4', border: '#BBF7D0' },
                    'Hatótáv':    { icon: '📍', bg: '#FDF2F8', border: '#F9A8D4' },
                    'Max sebesség': { icon: '💨', bg: '#EFF6FF', border: '#BFDBFE' },
                  };
                  const style = ICONS[label] || { icon: '📋', bg: 'var(--bg)', border: 'var(--border)' };
                  return (
                    <div key={i} style={{ background: style.bg, border: `1px solid ${style.border}`, borderRadius: 'var(--r-lg)', padding: 'var(--sp-4)', display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
                      <span style={{ fontSize: 22 }}>{style.icon}</span>
                      <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)', fontWeight: 500 }}>{label}</span>
                      <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--text)', fontWeight: 700 }}>{value}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {similar.length > 0 && (
            <div style={{ marginTop: 'var(--sp-10)', paddingTop: 'var(--sp-8)', borderTop: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 'var(--sp-3)', marginBottom: 'var(--sp-6)' }}>
                <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--fs-xl)', fontWeight: 700, color: 'var(--text)' }}>Hasonló kerékpárok</h2>
                <Link to={`/kerekparok?category=${product.category_slug}`} style={{ fontSize: 'var(--fs-sm)', color: 'var(--coral)', fontWeight: 600 }}>Összes megtekintése →</Link>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 'var(--sp-4)' }}>
                {similar.map(p => <ProductCard key={p.id} product={p} />)}
              </div>
            </div>
          )}
        </div>
      </main>
    </>
  );
}
